import React, { useState, useCallback, useRef } from "react";
import * as XLSX from "xlsx";
import "./AiDescGenerator.css";

// ─── localStorage hook ────────────────────────────────────────────────────────

function useLocalStorage(key, defaultValue) {
  const [value, setValue] = useState(() => {
    try {
      const raw = localStorage.getItem(key);
      return raw !== null ? JSON.parse(raw) : defaultValue;
    } catch {
      return defaultValue;
    }
  });

  const set = useCallback(
    (next) => {
      const resolved = typeof next === "function" ? next(value) : next;
      setValue(resolved);
      try {
        localStorage.setItem(key, JSON.stringify(resolved));
      } catch {}
    },
    [key, value]
  );

  return [value, set];
}

// ─── Constants ────────────────────────────────────────────────────────────────

const SCOPES = [
  { value: "GIGACHAT_API_PERS", label: "Физическое лицо — личный ключ" },
  { value: "GIGACHAT_API_CORP", label: "Корпоративный доступ (CORP)" },
  { value: "GIGACHAT_API_B2B",  label: "B2B-доступ" },
];

const MODELS = [
  { value: "GigaChat",      label: "GigaChat" },
  { value: "GigaChat-Plus", label: "GigaChat-Plus" },
  { value: "GigaChat-Pro",  label: "GigaChat-Pro" },
  { value: "GigaChat-Max",  label: "GigaChat-Max" },
];

const DEFAULT_PROMPTS = {
  keys: "Сделай список ключевых запросов для товара. Не переспамливай и добавляй только максимально релевантные запросы. Учитывай название, бренд, материалы и свойства товара. Обязательно используй разные способы написания брендов, включая варианты на русском, английском языках и возможные опечатки, если не знаешь такие варианты — переводи принудительно наименование бренда с английского на русский или с русского на английский и используй оба варианта, например Kosadaka = Косадака. Учитывай разный порядок слов и словоформы, синонимы и т.п. Делай перечисления разных возможностей запросов покупателей. Просто список ключевых фраз через точку с запятой, без кавычек, без HTML, без нумерации и лишнего текста. Эмодзи и иконки запрещены. Пример: ключ1; ключ2; ключ3.",
  ozon_title: "Создай одно SEO-название для OZON по правилам: Бренд (если есть) + Тип товара + Название (если есть) + Важные характеристики. Супер строгое ограничение всего названия — 200 символов. Не ставь . или другие знаки в конце. Эмодзи и иконки запрещены. Убери упоминания подарков.",
  ozon_desc: "Создай одно описание для OZON, используя принципы нейромаркетинга. Пиши развёрнуто и подробно — раскрывай преимущества, материалы, сценарии использования, целевую аудиторию. Целевой объём — от 3000 до 6000 символов, используй объём по максимуму. Читабельное, без спама. Добавь ключевые фразы из списка с учётом их словоформы и порядка слов. Разрешены теги только: <br>, <ul>, <li>. СТРОЖАЙШЕ Запрещены теги заголовков H и теги утолщения <strong>, <b>, * или курсива, <em>, <p>. Эмодзи и иконки запрещены. Не ставь точку в конце. Проверяй, чтобы описание не обрывалось, а последнее предложение было логично и закончено. Убери упоминания подарков.",
  wb_title: "Создай одно SEO-название для Wildberries: Тип товара + Название модели (если есть) + Важные характеристики. Без бренда. Ограничение 60 символов (идеально 40). HTML и выделения текста запрещены. Эмодзи и иконки запрещены. Убери упоминания подарков.",
  wb_desc: "Создай одно описание для Wildberries, используя принципы нейромаркетинга. Пиши развёрнуто и подробно — раскрывай преимущества, материалы, сценарии использования, целевую аудиторию. Целевой объём — от 2500 до 5000 символов, используй объём по максимуму. Читабельное, без спама. Добавь ключевые фразы из списка с учётом их словоформы и порядка слов. Разрешены теги только: <br>, <ul>, <li>. СТРОЖАЙШЕ Запрещены теги заголовков H и теги утолщения <strong>, <b>, * или курсива, <em>, <p>. Эмодзи и иконки запрещены. Не ставь точку в конце. Проверяй, чтобы описание не обрывалось, а последнее предложение было логично и закончено. Убери упоминания подарков.",
};

const PROMPT_LABELS = {
  keys:       "Генерация ключевых запросов",
  ozon_title: "Название OZON",
  ozon_desc:  "Описание OZON",
  wb_title:   "Название Wildberries",
  wb_desc:    "Описание Wildberries",
};

let _rowId = 0;
function newRow(overrides = {}) {
  return { id: ++_rowId, article: "", name: "", description: "", brand: "", keywords: "", ...overrides };
}

// ─── Parse sheet rows (xlsx or TSV) ──────────────────────────────────────────

function parseSheetRows(matrix) {
  if (!matrix.length) return [];
  const firstCells = matrix[0].map((c) => String(c ?? "").toLowerCase());
  const knownHeaders = ["артикул", "article", "название", "name", "описание", "бренд", "brand", "ключевые", "key"];
  const hasHeader = firstCells.some((c) => knownHeaders.some((h) => c.includes(h)));
  const dataRows = hasHeader ? matrix.slice(1) : matrix;
  return dataRows
    .filter((row) => row.some((c) => String(c ?? "").trim()))
    .map((row) =>
      newRow({
        article:     String(row[0] ?? "").trim(),
        name:        String(row[1] ?? "").trim(),
        description: String(row[2] ?? "").trim(),
        brand:       String(row[3] ?? "").trim(),
        keywords:    String(row[4] ?? "").trim(),
      })
    );
}

// ─── Copy helper ──────────────────────────────────────────────────────────────

function CopyBtn({ text }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };
  return (
    <button className="aig__copy-btn" onClick={copy} title="Копировать">
      {copied ? "✓" : "⎘"}
    </button>
  );
}

// ─── Result card ──────────────────────────────────────────────────────────────

function ResultCard({ row, result }) {
  const label = row.name || row.article || `Строка ${row.id}`;
  const status = result?.status ?? "pending";

  return (
    <div className={`aig__card aig__card--${status}`}>
      <div className="aig__card-header">
        <span className="aig__card-label">{label}</span>
        {row.article && <span className="aig__card-art">{row.article}</span>}
        <span className={`aig__card-status aig__card-status--${status}`}>
          {status === "loading" && <><span className="aig__spinner" />{result?.phase === "keys" ? " Ключи..." : " Контент..."}</>}
          {status === "done"    && "✓ Готово"}
          {status === "error"   && "✗ Ошибка"}
          {status === "pending" && "—"}
        </span>
      </div>

      {status === "error" && (
        <div className="aig__card-error">{result.error}</div>
      )}

      {status === "done" && result.data && (
        <div className="aig__card-fields">
          {result.data.keys && (
            <ResultField label="Ключевые запросы" value={result.data.keys} multiline />
          )}
          {result.platforms?.ozon && <>
            <ResultField label="Название OZON"  value={result.data.ozon_title} limit={200} />
            <ResultField label="Описание OZON"  value={result.data.ozon_desc}  multiline limit={6000} />
          </>}
          {result.platforms?.wb && <>
            <ResultField label="Название WB"    value={result.data.wb_title}   limit={60} ideal={40} />
            <ResultField label="Описание WB"    value={result.data.wb_desc}    multiline limit={5000} />
          </>}
        </div>
      )}
    </div>
  );
}

function ResultField({ label, value, multiline, limit, ideal }) {
  const len = value?.length ?? 0;
  const overLimit = limit && len > limit;
  const overIdeal = ideal && len > ideal;

  return (
    <div className="aig__field">
      <div className="aig__field-header">
        <span className="aig__field-label">{label}</span>
        {limit && (
          <span className={`aig__field-count ${overLimit ? "aig__field-count--over" : overIdeal ? "aig__field-count--warn" : ""}`}>
            {len}/{limit}
          </span>
        )}
        <CopyBtn text={value} />
      </div>
      {multiline ? (
        <textarea className="aig__field-textarea" readOnly value={value} rows={Math.min(8, Math.ceil(len / 80) + 1)} />
      ) : (
        <input className="aig__field-input" readOnly value={value} />
      )}
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function AiDescGenerator() {
  const [apiKey, setApiKey]   = useLocalStorage("giga_api_key", "");
  const [scope, setScope]     = useLocalStorage("giga_scope", "GIGACHAT_API_PERS");
  const [model, setModel]     = useLocalStorage("giga_model", "GigaChat");
  const [prompts, setPrompts] = useLocalStorage("giga_prompts", DEFAULT_PROMPTS);

  const [rows, setRows]         = useState([newRow()]);
  const [results, setResults]   = useState({});

  const [platforms, setPlatforms] = useLocalStorage("giga_platforms", { ozon: true, wb: true });

  const [keyPanelOpen, setKeyPanelOpen] = useState(false);
  const [showKeyText, setShowKeyText]   = useState(false);
  const [showPrompts, setShowPrompts]   = useState(false);
  const [generating, setGenerating]     = useState(false);

  const abortRef          = useRef(false);
  const abortControllerRef = useRef(null);
  const fileInputRef      = useRef(null);

  // ── Rows CRUD ──────────────────────────────────────────────────────────────

  const addRow = () => setRows((r) => [...r, newRow()]);

  const deleteRow = (id) => setRows((r) => r.filter((x) => x.id !== id));

  const updateRow = (id, field, value) =>
    setRows((r) => r.map((x) => (x.id === id ? { ...x, [field]: value } : x)));

  // ── File upload (xlsx / xls / csv) ────────────────────────────────────────

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";

    const reader = new FileReader();
    reader.onload = (evt) => {
      const data = new Uint8Array(evt.target.result);
      const wb   = XLSX.read(data, { type: "array" });
      const ws   = wb.Sheets[wb.SheetNames[0]];
      const matrix = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });
      const parsed = parseSheetRows(matrix);
      if (parsed.length) setRows(parsed);
    };
    reader.readAsArrayBuffer(file);
  };

  // ── Paste from Excel / Google Sheets ──────────────────────────────────────

  const handlePaste = (e) => {
    const text = e.clipboardData?.getData("text");
    if (!text || !text.includes("\t")) return;
    e.preventDefault();
    const matrix = text.trim().split(/\r?\n/).map((l) => l.split("\t"));
    const parsed = parseSheetRows(matrix);
    if (parsed.length) setRows(parsed);
  };

  // ── API call helper ────────────────────────────────────────────────────────

  const callApi = async (row, keywordsOverride, fields) => {
    const controller = new AbortController();
    abortControllerRef.current = controller;

    const res = await fetch("/api/v1/free/gigachat/generate", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      signal: controller.signal,
      body: JSON.stringify({
        api_key: apiKey.trim(),
        scope, model,
        article: row.article,
        name: row.name,
        description: row.description,
        brand: row.brand,
        keywords: keywordsOverride ?? row.keywords,
        prompts,
        fields,
      }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ detail: `HTTP ${res.status}` }));
      throw new Error(err.detail || `HTTP ${res.status}`);
    }
    return res.json();
  };

  // ── Generate ───────────────────────────────────────────────────────────────

  const generate = async () => {
    if (!apiKey.trim()) return alert("Введите API-ключ GigaChat");
    if (!platforms.ozon && !platforms.wb) return alert("Выберите хотя бы одну платформу");
    if (rows.every((r) => !r.name && !r.article && !r.description))
      return alert("Заполните хотя бы одну строку");

    abortRef.current = false;
    setGenerating(true);
    setResults({});

    // Content fields depend on platform selection
    const contentFields = [
      ...(platforms.ozon ? ["ozon_title", "ozon_desc"] : []),
      ...(platforms.wb   ? ["wb_title",   "wb_desc"]   : []),
    ];

    for (const row of rows) {
      if (abortRef.current) break;
      try {
        let keywords = row.keywords.trim();

        // Step 1: generate keys if field is empty
        if (!keywords) {
          setResults((p) => ({ ...p, [row.id]: { status: "loading", phase: "keys" } }));
          const keysData = await callApi(row, "", ["keys"]);
          keywords = keysData.keys || "";
        }

        // Step 2: generate content for selected platforms
        setResults((p) => ({ ...p, [row.id]: { ...(p[row.id] || {}), status: "loading", phase: "content" } }));
        const contentData = await callApi(row, keywords, contentFields);

        setResults((p) => ({
          ...p,
          [row.id]: {
            status: "done",
            platforms,
            data: { keys: keywords, ...contentData },
          },
        }));
      } catch (ex) {
        if (ex.name === "AbortError") break;
        setResults((p) => ({ ...p, [row.id]: { status: "error", error: ex.message } }));
      }
    }
    setGenerating(false);
  };

  const stopGenerate = () => {
    abortRef.current = true;
    abortControllerRef.current?.abort();
  };

  const resetPrompts = () => setPrompts(DEFAULT_PROMPTS);

  // ── Download template ─────────────────────────────────────────────────────

  const downloadTemplate = () => {
    const ws = XLSX.utils.aoa_to_sheet([
      ["Артикул", "Название", "Описание / характеристики", "Бренд", "Ключевые запросы"],
      ["АРТ-001", "Пример товара", "Материал: хлопок, размер: 42", "Бренд", ""],
    ]);
    ws["!cols"] = [{ wch: 16 }, { wch: 30 }, { wch: 50 }, { wch: 20 }, { wch: 40 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Товары");
    XLSX.writeFile(wb, "шаблон_товары.xlsx");
  };

  // ── Export XLSX ────────────────────────────────────────────────────────────

  const exportXlsx = () => {
    const data = rows
      .filter((r) => results[r.id]?.status === "done")
      .map((r) => {
        const d = results[r.id].data;
        return {
          "Артикул":          r.article,
          "Название":         r.name,
          "Ключевые запросы": d.keys,
          "Название OZON":    d.ozon_title,
          "Описание OZON":    d.ozon_desc,
          "Название WB":      d.wb_title,
          "Описание WB":      d.wb_desc,
        };
      });
    const ws = XLSX.utils.json_to_sheet(data);
    // Wider columns for description fields
    ws["!cols"] = [
      { wch: 16 }, { wch: 30 }, { wch: 50 },
      { wch: 40 }, { wch: 80 }, { wch: 30 }, { wch: 80 },
    ];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Результаты");
    XLSX.writeFile(wb, "ai_descriptions.xlsx");
  };

  const doneCount = rows.filter((r) => results[r.id]?.status === "done").length;
  const hasResults = doneCount > 0;
  const tokenEstimate = rows.length * 1000;

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="aig">

      {/* ── Инструкция получения ключа ── */}
      <div className="aig__instruction">
        <div className="aig__instruction-icon">ℹ</div>
        <div className="aig__instruction-body">
          <strong>Как получить API-ключ GigaChat:</strong>
          <ol className="aig__instruction-steps">
            <li>Перейдите на <a href="https://developers.sber.ru/studio/" target="_blank" rel="noopener noreferrer">developers.sber.ru/studio</a> и авторизуйтесь.</li>
            <li>Создайте новый проект и выберите <strong>GigaChat API</strong>.</li>
            <li>Перейдите в раздел <strong>«Настроить API»</strong> и нажмите <strong>«Сгенерировать Authorization key»</strong>.</li>
            <li>Скопируйте ключ и вставьте его в настройки ниже.</li>
          </ol>
          <p className="aig__instruction-note">~1 000 токенов на один артикул · Тип доступа «Физическое лицо» подходит для большинства личных ключей</p>
        </div>
      </div>

      {/* ── API Key ── */}
      <section className="aig__section">
        <button className="aig__collapse-btn" onClick={() => setKeyPanelOpen((v) => !v)}>
          <span>Настройки API</span>
          <span className={`aig__chevron ${keyPanelOpen ? "aig__chevron--open" : ""}`}>▾</span>
          {apiKey && !keyPanelOpen && <span className="aig__badge aig__badge--ok">ключ задан</span>}
          {!apiKey && !keyPanelOpen && <span className="aig__badge aig__badge--warn">ключ не задан</span>}
        </button>

        {keyPanelOpen && (
          <div className="aig__panel">
            <p className="aig__hint">
              Ключ сохраняется только в вашем браузере (localStorage). На наш сервер не сохраняется — только транзитом форвардится в GigaChat через HTTPS.
            </p>
            <div className="aig__key-row">
              <label className="aig__label">Authorization Key</label>
              <div className="aig__key-wrap">
                <input
                  className="aig__input"
                  type={showKeyText ? "text" : "password"}
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  placeholder="Basic ..."
                  autoComplete="off"
                />
                <button className="aig__eye-btn" onClick={() => setShowKeyText((v) => !v)}>
                  {showKeyText ? "скрыть" : "показать"}
                </button>
              </div>
            </div>
            <div className="aig__row">
              <div className="aig__col">
                <label className="aig__label">
                  Тип доступа
                  <span className="aig__label-hint"> — указан в личном кабинете GigaChat Studio</span>
                </label>
                <select className="aig__select" value={scope} onChange={(e) => setScope(e.target.value)}>
                  {SCOPES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </div>
              <div className="aig__col">
                <label className="aig__label">
                  Модель
                  <span className="aig__label-hint"> — доступность зависит от подписки</span>
                </label>
                <select className="aig__select" value={model} onChange={(e) => setModel(e.target.value)}>
                  {MODELS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                </select>
              </div>
            </div>
          </div>
        )}
      </section>

      {/* ── Промпты ── */}
      <section className="aig__section">
        <button className="aig__collapse-btn" onClick={() => setShowPrompts((v) => !v)}>
          <span>Настройки промптов</span>
          <span className={`aig__chevron ${showPrompts ? "aig__chevron--open" : ""}`}>▾</span>
        </button>

        {showPrompts && (
          <div className="aig__panel">
            {Object.keys(DEFAULT_PROMPTS).map((key) => (
              <div key={key} className="aig__prompt-field">
                <label className="aig__label">{PROMPT_LABELS[key]}</label>
                <textarea
                  className="aig__textarea"
                  rows={4}
                  value={prompts[key] ?? ""}
                  onChange={(e) => setPrompts((p) => ({ ...p, [key]: e.target.value }))}
                />
              </div>
            ))}
            <button className="aig__btn aig__btn--ghost" onClick={resetPrompts}>
              Сбросить к умолчаниям
            </button>
          </div>
        )}
      </section>

      {/* ── Таблица товаров ── */}
      <section className="aig__section">
        <div className="aig__table-header">
          <h3 className="aig__section-title">Товары для генерации</h3>
          <div className="aig__table-actions">
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls,.csv"
              style={{ display: "none" }}
              onChange={handleFileUpload}
            />
            <button className="aig__btn aig__btn--ghost aig__btn--sm" onClick={downloadTemplate}>
              ⬇ Скачать шаблон
            </button>
            <button className="aig__btn aig__btn--ghost aig__btn--sm" onClick={() => fileInputRef.current?.click()}>
              ↑ Загрузить файл (.xlsx / .csv)
            </button>
            <button className="aig__btn aig__btn--ghost aig__btn--sm" onClick={addRow}>
              + Строка
            </button>
          </div>
        </div>

        <p className="aig__table-hint">
          Колонки в файле или при вставке из Excel: <strong>Артикул → Название → Описание/характеристики → Бренд → Ключевые запросы</strong>
        </p>

        <div className="aig__table-wrap" onPaste={handlePaste}>
          <table className="aig__table">
            <thead>
              <tr>
                <th>Артикул</th>
                <th>Название *</th>
                <th>Описание / характеристики</th>
                <th>Бренд</th>
                <th>Ключевые запросы</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id}>
                  {["article", "name", "description", "brand", "keywords"].map((field) => (
                    <td key={field}>
                      {field === "description" ? (
                        <textarea
                          className="aig__cell-textarea"
                          value={row[field]}
                          onChange={(e) => updateRow(row.id, field, e.target.value)}
                          rows={2}
                        />
                      ) : (
                        <input
                          className="aig__cell-input"
                          value={row[field]}
                          onChange={(e) => updateRow(row.id, field, e.target.value)}
                        />
                      )}
                    </td>
                  ))}
                  <td>
                    <button className="aig__del-btn" onClick={() => deleteRow(row.id)} title="Удалить строку">✕</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* ── Платформы ── */}
      <div className="aig__platforms">
        <span className="aig__platforms-label">Генерировать для:</span>
        <label className={`aig__platform-toggle ${platforms.ozon ? "aig__platform-toggle--on" : ""}`}>
          <input
            type="checkbox"
            checked={platforms.ozon}
            onChange={(e) => setPlatforms((p) => ({ ...p, ozon: e.target.checked }))}
          />
          Ozon
        </label>
        <label className={`aig__platform-toggle ${platforms.wb ? "aig__platform-toggle--on" : ""}`}>
          <input
            type="checkbox"
            checked={platforms.wb}
            onChange={(e) => setPlatforms((p) => ({ ...p, wb: e.target.checked }))}
          />
          Wildberries
        </label>
        <span className="aig__platforms-hint">
          Если «Ключевые запросы» в строке пусты — они генерируются автоматически перед контентом
        </span>
      </div>

      {/* ── Кнопка генерации ── */}
      <div className="aig__generate-bar">
        {!generating ? (
          <button className="aig__btn aig__btn--primary aig__btn--lg" onClick={generate} disabled={!apiKey.trim()}>
            ✦ Генерировать{rows.length > 1 ? ` ${rows.length} товаров` : ""}
          </button>
        ) : (
          <button className="aig__btn aig__btn--danger aig__btn--lg" onClick={stopGenerate}>
            ⏹ Остановить
          </button>
        )}
        {hasResults && (
          <button className="aig__btn aig__btn--ghost" onClick={exportXlsx}>
            ⬇ Скачать .xlsx ({doneCount})
          </button>
        )}
        {generating && (
          <span className="aig__progress">{doneCount} / {rows.length}</span>
        )}
        <span className="aig__token-hint">~{tokenEstimate.toLocaleString("ru")} токенов</span>
      </div>

      {/* ── Результаты ── */}
      {rows.some((r) => results[r.id]) && (
        <section className="aig__section aig__results">
          <h3 className="aig__section-title">Результаты</h3>
          {rows.map((row) =>
            results[row.id] ? <ResultCard key={row.id} row={row} result={results[row.id]} /> : null
          )}
        </section>
      )}
    </div>
  );
}
